/* eslint-disable no-use-before-define */
import React, { useState, useEffect, useRef, useCallback } from "react";
import TextField from "@material-ui/core/TextField";
import Autocomplete, {
  createFilterOptions
} from "@material-ui/lab/Autocomplete";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import { Checkbox } from "@material-ui/core";
import CircularProgress from '@material-ui/core/CircularProgress';
import AddIcon from '@material-ui/icons/Add';
import ClearIcon from '@material-ui/icons/Clear';
import RemoveIcon from '@material-ui/icons/Remove';
import Chip from '@material-ui/core/Chip';
import debounce from "lodash.debounce";

import API from "../Utility";
import ConfirmationModal from "../ConfirmationModal";

const selectAllOutsideInput = false;
let returnAllResultsAtOnce = true;

const MultiSelectTypeahead = ({
  name,
  displayName,
  url,
  label,
  placeholder,
  selectAllLabel,
  noOptionsText,
  limitTags,
  onChange,
  defaultValue,
  disabledOptions,
  className,
  onFocus,
  onBlur,
  items,
  allowNewItems,
  disabled,
  multiple = true,
  preventSelectAll,
  dynamicHeight,
  onNewItemAdded,
  resetInputAfterSelectOrRemove,
  initApiSearchOnChange,
  mapResponse,
  minSearchLength = 2,
  showItemsOnFocus = false,
  disableCloseOnSelect = false
}) => {
  function arrayUnique(array) {
    var a = array.concat();
    for (var i = 0; i < a.length; ++i) {
      for (var j = i + 1; j < a.length; ++j) {
        if (a[i] === a[j]) a.splice(j--, 1);
      }
    }

    return a;
  }

  const [selectedOptions, setSelectedOptions] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [showClearInputModal, setShowClearInputModal] = useState(false);

  const [open, setOpen] = useState(false);
  const [fetchingData, setFetchingDate] = useState(false);
  const [options, setOptions] = useState([]);
  const [noOptionsMessage, setNoOptionsMessage] = useState(noOptionsText);
  const previousController = useRef();
  let delay = 450;

  if (initApiSearchOnChange) {
    returnAllResultsAtOnce = false;
  }

  useEffect(() => {
    setOptions(items);
  }, [items]);

  // Set up preselected values
  useEffect(() => {
    if (defaultValue && defaultValue.length > 0) {
      setSelectedOptions(arrayUnique([...selectedOptions, ...defaultValue]));
    }
  }, [defaultValue]);

  useEffect(() => {
    if (!open) {
      setFetchingDate(false);
    }
  }, [open]);

  // Call API to fetch typeahead list
  const getData = async(searchTerm = null) => {
    setFetchingDate(true);
    if (previousController.current) {
      previousController.current.abort();
    }
    const controller = new AbortController();
    const signal = controller.signal;
    previousController.current = controller;

    if (url) {
      setNoOptionsMessage("Loading...");
      let response = await API.get({
        url: url,
        signal,
        params: {
          page: 1,
          sortDir: 1,
          nameMatch: searchTerm,
        },
      });
      if (response.filteredResults) {
        if (response.filteredResults.length > 0) {
          if (mapResponse) {
            response.filteredResults = mapResponse(response.filteredResults);
          }
          setOptions(response.filteredResults);
        } else {
          setOptions([]);
          setNoOptionsMessage(noOptionsText);
        }
      } else if (response.err) {
        setOptions([]);
        setNoOptionsMessage(noOptionsText);
      }
      setFetchingDate(false);
    } else if (items?.length > 0) {
      setOptions(items);
      setFetchingDate(false);
    } else {
      setOptions([]);
      setFetchingDate(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce(nextValue => onInputChange(nextValue), delay),
    [], // will be created only once initially
  );

  const handleSearch = (event, value, reason) => {
    if (event) {
      event.persist();
      const { value: nextValue } = event && event.target ? event.target : undefined;
      // Even though handleSearch is created on each render and executed
      // it references the same debouncedSearch that was created initially
      if (event && event.type === "blur") {
        if (!selectAllOutsideInput) {
          setInputValue("");
        }
      } else if (reason !== "reset") {
        setInputValue(value);
        if (!returnAllResultsAtOnce) {
          debouncedSearch(nextValue);
        }
      }
    } else if (reason !== "reset") {
      setInputValue(value);
    }
  };

  const onInputChange = (value) => {
    if (value?.length === 0 || value?.length >= minSearchLength) {
      if (!returnAllResultsAtOnce) {
        getData(value);
      }
    } else {
      if (url?.length > 0) {
        setOptions([]);
      } else if (items?.length > 0) {
        setOptions(items);
      }
    }
  };

  useEffect(() => {
    let active = true;

    if (!fetchingData) {
      return undefined;
    }

    (async() => {
      if (active) {
        setOptions([...options]);
      }
    })();

    return () => {
      active = false;
    };
  }, [fetchingData]);

  useEffect(() => {
    if (!open) {
      setOptions([]);
      if (!returnAllResultsAtOnce) {
        onInputChange("");
      } else {
        getData("");
      }
    }
  }, [open]);

  let currentFilter = [];
  const setCurrentFilter = (fi) => {
    currentFilter = fi;
  };

  let allSelected = options.length === selectedOptions.length;
  const handleToggleOption = (selectedOptions) => {
    setSelectedOptions(selectedOptions);
  };
  const handleClearOptions = () => {
    setSelectedOptions([]);
  };
  const getOptionLabel = (option) => `${option.name}`;
  const handleSelectAll = (isSelected, callback) => {
    if (isSelected) {
      if (currentFilter.length > 0) {
        let allOptions = arrayUnique([...selectedOptions, ...currentFilter]);
        setSelectedOptions(allOptions);
        setCurrentFilter([]);
        callback(allOptions);
      }
    } else {
      setSelectedOptions(selectedOptions);
      handleClearOptions();
      callback(selectedOptions);
    }
  };

  const handleToggleSelectAll = (callback) => {
    handleSelectAll && handleSelectAll(!allSelected, (allSelectedOptions) => {
      callback(allSelectedOptions);
    });
  };

  const confirmClearInput = async(event, exitValue) => {
    if (exitValue === true) {
      handleClearOptions && handleClearOptions();
      setShowClearInputModal(false);
      onChange([]);
    } else {
      setShowClearInputModal(false);
    }
  };

  const handleChange = (event, selectedOptions, reason) => {
    if (reason === "select-option" || reason === "remove-option") {
      if (Array.isArray(selectedOptions) && selectedOptions.find((option) => option._id === "select-all")) {
        handleToggleSelectAll((allSelectedOptions) => {
          let result = [];
          result = allSelectedOptions.filter((el) => el._id !== "select-all");
          return onChange(result);
        });
      } else if (Array.isArray(selectedOptions) && selectedOptions.find((option) => option._id === "add-new")) {
        let result = [];
        result = selectedOptions.map((el) => {
          let newItem = {
            name: el.inputValue,
            inputValue: el.inputValue
          };
          if (onNewItemAdded) {
            onNewItemAdded(newItem);
          }
          if (el._id === "add-new") {
            return newItem;
          } else {
            return el;
          }
        });
        setOpen(false);
        setInputValue("");
        return onChange(result);
      } else {
        handleToggleOption && handleToggleOption(selectedOptions);
        if (resetInputAfterSelectOrRemove) {
          setInputValue("");
        }
        return onChange(selectedOptions);
      }
    } else if (reason === "clear") {
      setShowClearInputModal(true);
    }
  };
  const optionRenderer = (option, { selected }) => {
    if (currentFilter && currentFilter.length > 0) {
      allSelected = true;
      currentFilter.forEach((item) => {
        if (
          selectedOptions.findIndex((selopt) => {
            return selopt._id === item._id;
          }) === -1
        ) {
          allSelected = false;
        }
      });
    } else {
      allSelected = options.length === selectedOptions.length;
    }
    const selectAllProps =
      option._id === "select-all" // To control the state of 'select-all' checkbox
        ? { checked: allSelected }
        : {};
    return (
      <>
        {/* <Checkbox
          color="primary"
          icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
          checkedIcon={<CheckBoxIcon fontSize="small" />}
          style={{ marginRight: 8 }}
          checked={selected}
          {...selectAllProps}
        /> */}
        {getOptionLabel(option)}
      </>
    );
  };
  const inputRenderer = (params) => {
    const { InputProps, ...restParams } = params;
    const { endAdornment, startAdornment, ...restInputProps } = InputProps;
    return (
      <TextField
        disabled={disabled}
        style={{ pointerEvents: 'auto', height: dynamicHeight ? 'auto' : '' }}
        name={name}
        onFocus={onFocus}
        onBlur={onBlur}
        variant="outlined"
        {...restParams}
        label={label}
        placeholder={placeholder}
        InputProps={{
          ...restInputProps,
          startAdornment: (
            <div className='typeahead-overflow-wrapper' style={{
              maxHeight: '110px',
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
            >
              {startAdornment}
            </div>
          ),
          endAdornment: (
            <React.Fragment>
              {fetchingData ? <CircularProgress color="inherit" size={20} /> : null}
              {endAdornment}
            </React.Fragment>
          ),
        }}
      />
    );
  };
  const getOptionSelected = (option, anotherOption) =>
    option._id === anotherOption._id;
  const filter = createFilterOptions();
  return (
    <>
      {selectAllOutsideInput && !preventSelectAll && (
        <button
          onClick={() => {
            setSelectedOptions(
              arrayUnique([...selectedOptions, ...currentFilter])
            );
          }}
        >
          Select All
        </button>
      )}
      {dynamicHeight}
      <Autocomplete
        disabled={disabled}
        style={{ cursor: 'text', height: dynamicHeight ? 'auto' : '' }}
        classes={{ inputRoot: className }}
        autoComplete={true}
        autoHighlight={true}
        openOnFocus={showItemsOnFocus}
        id={name}
        name={name}
        multiple={multiple}
        sx={{ width: 300 }}
        // open={open}
        open={showItemsOnFocus || inputValue?.length >= minSearchLength ? open : false}
        disableCloseOnSelect={disableCloseOnSelect}
        filterSelectedOptions
        size="small"
        limitTags={limitTags}
        options={options}
        disablePortal
        popupIcon={open && inputValue?.length >= minSearchLength ? <RemoveIcon style={{ pointerEvents: 'auto' }} /> : <AddIcon style={{ pointerEvents: 'auto' }} />}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        loading={fetchingData}
        value={selectedOptions}
        inputValue={inputValue}
        defaultValue={defaultValue}
        getOptionLabel={getOptionLabel}
        getOptionSelected={getOptionSelected}
        noOptionsText={noOptionsMessage}
        handleHomeEndKeys={allowNewItems}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);
          let filteredWithoutUndefined = filtered.filter((item) => {
            return item.name;
          });
          if (allowNewItems) {
            const isExisting = options.some((option) => params.inputValue === option.title);
            if (params.inputValue !== '' && !isExisting && filteredWithoutUndefined?.length === 0) {
              filteredWithoutUndefined.push({
                inputValue,
                name: `Add "${params.inputValue}"`,
                _id: "add-new"
              });
            }
          }
          setCurrentFilter(filteredWithoutUndefined);
          if (preventSelectAll) {
            return filteredWithoutUndefined.length > 1 ? [...filteredWithoutUndefined] : [...filteredWithoutUndefined];
          } else {
            return filteredWithoutUndefined.length > 1 ? [{ name: selectAllLabel, _id: "select-all" }, ...filteredWithoutUndefined] : [...filteredWithoutUndefined];
          }
        }}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              key={index}
              label={option.name}
              {...getTagProps({ index })}
              disabled={disabled || disabledOptions?.length > 0 && disabledOptions?.indexOf(option) !== -1}
              style={{ borderRadius: 5, cursor: 'pointer', pointerEvents: disabled || disabledOptions?.length > 0 && disabledOptions?.indexOf(option) !== -1 ? 'none' : 'auto' }}
            />
          ))
        }
        onInputChange={handleSearch}
        // onInputChange={(event, value, reason) => {
        //   if (event && event.type === "blur") {
        //     if (!selectAllOutsideInput) {
        //       setInputValue("");
        //     }
        //   } else if (reason !== "reset") {
        //     setInputValue(value);
        //     onInputChange(value);
        //   }
        // }}
        onChange={handleChange}
        renderOption={optionRenderer}
        renderInput={inputRenderer}
      />
      <ConfirmationModal
        title={`Clear ${displayName}`}
        body={
          <>
            <p>
              Are you sure you want to clear all?
            </p>
          </>
        }
        show={showClearInputModal}
        confirmtext={"Clear"}
        onHide={(event, exitValue) => confirmClearInput(event, exitValue)}
      />
    </>
  );
};

MultiSelectTypeahead.defaultProps = {
  limitTags: 5,
  options: [],
  selectedValues: [],
  getOptionLabel: (value) => value
};

export default MultiSelectTypeahead;
